/**
 * Title screen 3D background: a glowing primordial soup — drifting translucent cells with
 * nuclei, a spinning DNA double helix in front of a swirling portal, rising motes, fog.
 * Mouse/tilt parallax. Counts scale with quality tier.
 */
import {
  Color,
  FogExp2,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  MeshBasicMaterial,
  Object3D,
  PerspectiveCamera,
  PointLight,
  Scene,
  Vector3,
  AmbientLight,
  AdditiveBlending,
  SRGBColorSpace,
  TextureLoader,
  type Texture,
} from 'three';
import type { QualityProfile } from '@core/quality';
import { disposeTree, makeHelix, makeMotes, makePortal, membraneMaterial, uni } from './common';

interface Cell {
  pos: Vector3;
  vel: Vector3;
  scale: number;
  phase: number;
  spin: number;
}

export class TitleScene {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(50, 1, 0.1, 200);
  private cells: Cell[] = [];
  private cellMesh: InstancedMesh;
  private nucMesh: InstancedMesh;
  private helix: Group;
  private portal: ReturnType<typeof makePortal>;
  private motes: ReturnType<typeof makeMotes>;
  private membrane = membraneMaterial();
  private t = 0;
  private dummy = new Object3D();
  private pointer = { x: 0, y: 0, sx: 0, sy: 0 };
  private onMove = (e: PointerEvent) => {
    this.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
  };
  private plate?: Texture;
  private hasPlate = false;
  /** 0..1 zoom toward the portal (used when starting a new game). */
  plunge = 0;

  constructor(quality: QualityProfile) {
    const low = quality.tier === 'low';
    this.scene.background = new Color(0x020a07);
    // AI-generated key art (art/ux/cine) behind the live cells; the plate has its own helix +
    // vortex, so the 3D ones hide once it loads.
    new TextureLoader().load(`${import.meta.env.BASE_URL ?? '/'}ui/cine/title-plate.jpg`, (tex) => {
      tex.colorSpace = SRGBColorSpace;
      this.plate = tex;
      this.scene.background = tex;
      this.scene.backgroundIntensity = 0.7;
      this.portal.visible = false;
      this.hasPlate = true;
    });
    this.scene.fog = new FogExp2(0x03110b, 0.045);
    this.scene.add(new AmbientLight(0x8cffc0, 0.6));
    const light = new PointLight(0x97ff5a, 30, 30, 1.6);
    light.position.set(0, 0, 2);
    this.scene.add(light);

    // Portal behind the helix.
    this.portal = makePortal(4.2);
    this.portal.position.set(0, 0.6, -10);
    this.scene.add(this.portal);

    // DNA helix, gently tilted.
    this.helix = makeHelix({ turns: 3.2, height: 11, radius: 0.9, perTurn: low ? 14 : 22 });
    this.helix.position.set(3.4, 0, -2.4);
    this.helix.rotation.z = -0.35;
    this.scene.add(this.helix);

    // Drifting cells.
    const n = low ? 26 : quality.tier === 'medium' ? 44 : 64;
    const geo = new IcosahedronGeometry(1, low ? 2 : 3);
    this.cellMesh = new InstancedMesh(geo, this.membrane, n);
    this.nucMesh = new InstancedMesh(new IcosahedronGeometry(1, 1), new MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4, blending: AdditiveBlending, depthWrite: false }), n);
    const palette = [0x97ff5a, 0x3dffb0, 0x5bf0ff, 0xc9ff8a, 0x7cff6b, 0xffd86b];
    const c = new Color();
    for (let i = 0; i < n; i++) {
      const cell: Cell = {
        pos: new Vector3((Math.random() - 0.5) * 26, (Math.random() - 0.5) * 14, -Math.random() * 22 + 3),
        vel: new Vector3((Math.random() - 0.5) * 0.25, (Math.random() - 0.3) * 0.2, (Math.random() - 0.5) * 0.1),
        scale: 0.2 + Math.random() ** 2 * 0.9,
        phase: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.6,
      };
      this.cells.push(cell);
      c.setHex(palette[i % palette.length]);
      this.cellMesh.setColorAt(i, c);
      this.nucMesh.setColorAt(i, c.clone().lerp(new Color(0xffffff), 0.4));
    }
    this.scene.add(this.cellMesh, this.nucMesh);

    this.motes = makeMotes(low ? 120 : 320, new Vector3(30, 20, 24), 0xa8ff7a, 0.9);
    this.motes.position.z = -6;
    this.scene.add(this.motes);

    this.camera.position.set(0, 0, 9);
    window.addEventListener('pointermove', this.onMove);
    this.update(0);
  }

  /** Re-layout for aspect (helix moves in on narrow screens). */
  private layout(): void {
    const aspect = this.camera.aspect || 1.6;
    this.helix.position.x = aspect > 1.3 ? 3.2 + (aspect - 1.3) * 1.6 : 2.2;
    this.helix.visible = aspect > 0.9 && !this.hasPlate;
    // Cover-fit the background plate (16:9) to the viewport, with a slow drift + plunge zoom.
    if (this.plate) {
      const img = 16 / 9;
      const z = 1.06 + this.plunge * this.plunge * 0.6;
      const rx = (aspect > img ? 1 : aspect / img) / z;
      const ry = (aspect > img ? img / aspect : 1) / z;
      this.plate.repeat.set(rx, ry);
      this.plate.offset.set((1 - rx) / 2 + Math.sin(this.t * 0.07) * 0.01 + this.pointer.sx * 0.01, (1 - ry) / 2 - this.pointer.sy * 0.008);
    }
  }

  update(dt: number): void {
    this.t += dt;
    const t = this.t;
    this.layout();
    this.helix.rotation.y = t * 0.45;
    this.helix.position.y = Math.sin(t * 0.4) * 0.25;
    uni(this.portal).uTime.value = t;
    this.portal.rotation.z = t * 0.05;
    uni(this.motes).uTime.value = t;
    this.membrane.uniforms.uTime.value = t;

    const d = this.dummy;
    this.cells.forEach((cell, i) => {
      cell.pos.addScaledVector(cell.vel, dt);
      if (cell.pos.x > 14) cell.pos.x = -14;
      if (cell.pos.x < -14) cell.pos.x = 14;
      if (cell.pos.y > 8) cell.pos.y = -8;
      if (cell.pos.y < -8) cell.pos.y = 8;
      const wob = Math.sin(t * 1.3 + cell.phase);
      d.position.copy(cell.pos);
      d.position.y += Math.sin(t * 0.5 + cell.phase) * 0.3;
      d.rotation.set(t * cell.spin, t * cell.spin * 0.7, 0);
      d.scale.set(cell.scale * (1 + wob * 0.06), cell.scale * (1 - wob * 0.06), cell.scale);
      d.updateMatrix();
      this.cellMesh.setMatrixAt(i, d.matrix);
      d.scale.multiplyScalar(0.34);
      d.position.x += Math.sin(t * 0.7 + cell.phase) * cell.scale * 0.2;
      d.updateMatrix();
      this.nucMesh.setMatrixAt(i, d.matrix);
    });
    this.cellMesh.instanceMatrix.needsUpdate = true;
    this.nucMesh.instanceMatrix.needsUpdate = true;

    // Parallax + slow drift, plus the plunge zoom.
    const p = this.pointer;
    const k = 1 - Math.exp(-dt * 2.5);
    p.sx += (p.x - p.sx) * k;
    p.sy += (p.y - p.sy) * k;
    const ease = this.plunge * this.plunge;
    this.camera.position.set(p.sx * 0.6 + Math.sin(t * 0.13) * 0.4, -p.sy * 0.35 + Math.sin(t * 0.17) * 0.25, 9 - ease * 14);
    this.camera.lookAt(0, 0, -6);
    this.camera.rotation.z += ease * 1.2;
    uni(this.portal).uIntensity.value = 0.5 + ease * 2.5;
  }

  dispose(): void {
    window.removeEventListener('pointermove', this.onMove);
    disposeTree(this.scene);
  }
}
